export interface AbbreviationRange {
  divider: number;
  suffix: string;
}

export class NumberAbbreviator {
  private locale: string;

  constructor(locale: string = "en-AU") {
    this.locale = locale;
  }

  private getRanges(minFormatRange: number = 1e6): AbbreviationRange[] {
    // Default ranges for formatting large numbers with English suffixes
    const defaultRanges = [
      { divider: 1e9, suffix: "b" }, // Billion
      { divider: 1e6, suffix: "m" }, // Million
      { divider: 1e3, suffix: "k" }, // Thousand
    ].filter((item) => item.divider >= minFormatRange);

    const localeSpecificRanges: Record<string, AbbreviationRange[]> = {
      // Formatting yen (JPY) amounts in the Japanese locale
      "ja-JP": [
        { divider: 1e8, suffix: "億" }, // 100 million yen
        { divider: 1e4, suffix: "万" }, // 10,000 yen
        { divider: 1e3, suffix: "千" }, // 1,000 yen
      ],
    };

    return localeSpecificRanges[this.locale] || defaultRanges;
  }

  formatAbbreviatedNumber = (
    amount: number,
    {
      minFormatRange = 1e6,
      ...options
    }: Intl.NumberFormatOptions & { minFormatRange?: number } = {}
  ): string => {
    const ranges = this.getRanges(minFormatRange);

    // Use absolute value of amount to find the range
    const range = ranges.find((r) => Math.abs(amount) >= r.divider) || {
      divider: 1,
      suffix: "",
    };

    // Use absolute value of amount for formatting
    const formattedAmount = new Intl.NumberFormat(this.locale, options).format(
      Math.abs(amount) / range.divider
    );
    const sign = amount < 0 ? "-" : "";

    // Prepend a negative sign if the original amount was negative
    return `${sign}${formattedAmount}${range.suffix}`;
  };
}
